import { formatDateTime } from "@majitap/ui";
import { Badge } from "@majitap/ui/arc/badge/badge";
import { useCompanyFormat } from "../../company";
import { type DeliveryRecord, etaText, tripOf } from "../../deliveries";
import { DELIVERY_STATUS } from "../../labels";
import styles from "./delivery.module.css";

/**
 * Where the delivery is on its trip (`Console / Delivery`, Progress): every step with when it got
 * there. A delivery that left the main line (suspended, failed, cancelled, rerouted) says so.
 */
export function Tracker({ delivery: d }: { delivery: DeliveryRecord }) {
  const format = useCompanyFormat();
  const { steps, stopped } = tripOf(d);
  const [label, tone] = DELIVERY_STATUS[d.status];
  const latest = d.events[0];
  const next = steps.filter((s) => s.state === "todo").map((s) => s.label.toLowerCase());
  // P8-73: on the way, when it should reach the customer, and against its promise
  const eta = d.eta ? etaText(d.eta, format.timezone) : null;
  const promised = d.eta?.promised_by
    ? etaText({ ...d.eta, arrives_at: d.eta.promised_by }, format.timezone).clock
    : null;
  return (
    <section className={styles.tracker} aria-label="Progress">
      <div className={styles.trackerHead}>
        <span className={styles.trackerNow}>
          <Badge tone={tone}>{label}</Badge>
          {latest ? (
            <span className={styles.small}>
              since {formatDateTime(latest.occurred_at, format)}
              {latest.actor ? ` · ${latest.actor.name}` : ""}
            </span>
          ) : null}
        </span>
        {!stopped && next.length > 0 ? (
          <span className={d.eta?.late ? styles.late : styles.small}>
            Next:{" "}
            {eta
              ? `${next[0]} by about ${eta.clock}${promised ? ` (promised by ${promised}${d.eta?.late ? ", running late" : ""})` : ""}${next[1] ? `, then ${next[1]}` : ""}`
              : next.slice(0, 2).join(", then ")}
          </span>
        ) : null}
      </div>
      <ol className={styles.steps}>
        {steps.map((s) => (
          <li key={s.key} className={styles.step} data-state={s.state} data-stopped={stopped && s.state === "now"}>
            <span className={styles.stepDot} aria-hidden />
            <span className={styles.stepLabel}>{s.label}</span>
            <span className={eta && s.key === "at_customer" && !s.at ? styles.eta : styles.small}>
              {s.at ? formatDateTime(s.at, format) : eta && s.key === "at_customer" ? `~${eta.clock}` : "—"}
            </span>
          </li>
        ))}
      </ol>
    </section>
  );
}
